import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchTradeIconSvg } from '@apis/storage';
import type { IOptionChip } from '@components/Pages/AnalyticsPage/analytics.types';
import { professionTextColor } from '@utils/professionColor';
import { sanitizeSvg } from '@utils/sanitizeSvg';
import classes from './ProfessionChip.module.css';

interface IProfessionChipProps {
  chip: IOptionChip;
  /**
   * When true, the chip is followed by the contractor name in plain gray
   * text (matches trusstor's `ProfessionDisplayWithTradeIcon`). When false,
   * only the chip itself renders.
   */
  showContractor?: boolean;
}

/**
 * Trusstor-style profession chip — a coloured pill with an optional trade
 * pictogram and the trade/specialty text in white, paired with the
 * contractor name to the right in plain gray text.
 *
 * Mirrors `ProfessionDisplayWithTradeIcon` from the trusstor stack. The
 * per-trade SVG is pulled from the storage proxy at
 * `/api/sites/:siteId/storage/trade-icon/:tradeId`; when the upstream
 * 404s (no icon in the bucket for this trade) the chip renders text-only,
 * which matches trusstor's `TradeIconShared` fallback behaviour.
 */
export function ProfessionChip({ chip, showContractor = true }: IProfessionChipProps) {
  const { siteId } = useParams<{ siteId: string }>();
  const [svg, setSvg] = useState<string | null>(null);

  useEffect(() => {
    if (!siteId || !chip.tradeId) return;
    const ctrl = new AbortController();
    fetchTradeIconSvg(siteId, chip.tradeId, ctrl.signal)
      .then((s) => setSvg(s ? sanitizeSvg(recolor(s)) || null : null))
      .catch(() => setSvg(null));
    return () => ctrl.abort();
  }, [siteId, chip.tradeId]);

  return (
    <span className={classes.row}>
      <span
        className={classes.chip}
        // `--chip-bg` is consumed by the .chip rule — the hex comes from the
        // upstream profession record (no hex literals introduced here).
        // `--chip-fg` flips text/icon to dark on the light-gray fallback so the
        // label stays legible; stays white on a real brand colour.
        style={{
          ['--chip-bg' as string]: chip.background,
          ['--chip-fg' as string]: professionTextColor(chip.background),
        }}
      >
        {svg ? (
          <span
            className={classes.icon}
            aria-hidden="true"
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        ) : null}
        <span className={classes.text}>{chip.text}</span>
      </span>
      {showContractor && chip.contractor ? (
        <span className={classes.contractor}>{chip.contractor}</span>
      ) : null}
    </span>
  );
}

/**
 * Repaint coloured `fill`/`stroke` to `currentColor` so the inline SVG picks
 * up the chip's white foreground. `fill="none"` is preserved so outline-only
 * shapes stay outlines.
 */
function recolor(svg: string): string {
  return svg.replace(
    /(fill|stroke)="((?!none\b)[^"]+)"/g,
    (_match, attr: string) => `${attr}="currentColor"`,
  );
}
